import React, { useState, useEffect, useRef, useCallback } from 'react';
import JsBarcode from 'jsbarcode';
import { API_URL } from '../../services/api';
import { Printer, Search, X, Plus, Minus, Tag, ChevronDown } from 'lucide-react';

// ─── Composant étiquette individuelle 3.5cm × 1.4cm ──────────────────────────
const EtiquetteItem = React.forwardRef(({ bijou }, ref) => {
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (!barcodeRef.current) return;
    const val = bijou.id_produit || bijou.reference_article || '';
    if (!val) return;
    try {
      JsBarcode(barcodeRef.current, val, {
        format: 'CODE128',
        width: 1,
        height: 22,
        displayValue: false,
        margin: 0,
        background: '#ffffff',
        lineColor: '#000000',
      });
    } catch (e) {
      console.error('Barcode error:', e);
    }
  }, [bijou.id_produit, bijou.reference_article]);

  // Dériver le label métal depuis matiere ou couleur_or
  const getMetalLabel = () => {
    if (bijou.matiere && bijou.matiere.toUpperCase().includes('OR')) return bijou.matiere.toUpperCase();
    if (bijou.matiere && bijou.matiere.toUpperCase().includes('ARG')) return 'ARGENT';
    const couleur = (bijou.couleur_or || '').toUpperCase();
    if (couleur) return `OR ${couleur}`;
    return 'OR 18K';
  };

  // Extraire le carat depuis matiere (ex: "OR 18K" → "18K", "OR 18 CARATS" → "18K")
  const getCaratLabel = () => {
    const m = (bijou.matiere || '').toUpperCase();
    const match = m.match(/(\d{1,2})\s*[KK]|(\d{1,2})\s*CARAT/);
    if (match) return `${match[1] || match[2]}K`;
    // fallback depuis couleur_or ou description
    return '18K';
  };

  const prix = bijou.prix_vente
    ? `${parseFloat(bijou.prix_vente).toFixed(0)} DH`
    : '— DH';

  const metalLabel = getMetalLabel();
  const caratLabel = getCaratLabel();
  const refLabel = (bijou.reference_article || bijou.reference || '').substring(0, 10);

  return (
    <div
      ref={ref}
      className="etiquette-bijou"
      style={{
        width: '35mm',
        height: '14mm',
        display: 'flex',
        border: '0.5px solid #aaa',
        fontFamily: 'Arial, Helvetica, sans-serif',
        overflow: 'hidden',
        backgroundColor: '#fff',
        boxSizing: 'border-box',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
      }}
    >
      {/* ── Partie gauche : Métal + Prix ── */}
      <div
        style={{
          width: '50%',
          borderRight: '0.5px solid #ccc',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 1mm',
          gap: '0.5mm',
        }}
      >
        <div
          style={{
            fontSize: '6.5pt',
            fontWeight: 'bold',
            color: '#996515',
            letterSpacing: '0.3px',
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          {metalLabel}
        </div>
        <div
          style={{
            fontSize: '8pt',
            fontWeight: 'bold',
            color: '#111',
            textAlign: 'center',
            letterSpacing: '0.2px',
          }}
        >
          {prix}
        </div>
      </div>

      {/* ── Partie droite : Code-barre + Carat + Référence ── */}
      <div
        style={{
          width: '50%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.8mm 0.5mm 0.8mm 0.5mm',
        }}
      >
        {/* Code-barre */}
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <svg ref={barcodeRef} style={{ width: '100%', height: '22px' }}></svg>
        </div>
        {/* Carat + Référence */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            paddingTop: '0.3mm',
          }}
        >
          <span style={{ fontSize: '5pt', color: '#555', fontWeight: 'bold' }}>
            {caratLabel}
          </span>
          <span
            style={{
              fontSize: '4.5pt',
              color: '#333',
              fontWeight: 'bold',
              letterSpacing: '0.2px',
              maxWidth: '70%',
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {refLabel}
          </span>
        </div>
      </div>
    </div>
  );
});

// ─── Composant principal ──────────────────────────────────────────────────────
export default function ImpressionEtiquettes() {
  const [bijoux, setBijoux] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('stock'); // 'stock' | 'tous'
  const [selection, setSelection] = useState({}); // { bijouId: quantite }
  const [printReady, setPrintReady] = useState(false);
  const printRef = useRef(null);

  const headers = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  const fetchBijoux = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 2000 }); // §113c - max backend, import massif 1500-2000 bijoux
      const res = await fetch(`${API_URL}/bijoux?${params}`, { headers: headers() });
      const data = await res.json();
      setBijoux(Array.isArray(data) ? data : (data.bijoux || []));
    } catch (e) {
      console.error('Erreur fetch bijoux:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBijoux();
  }, [fetchBijoux]);

  // Filtrage liste
  const bijouFiltres = bijoux.filter(b => {
    const inStock = filtreStatut === 'tous' ? true : b.inventaire_actif === 1 || b.inventaire_actif === true;
    const q = search.toLowerCase();
    const matchSearch = !q
      || (b.id_produit || '').toLowerCase().includes(q)
      || (b.reference_article || '').toLowerCase().includes(q)
      || (b.reference || '').toLowerCase().includes(q)
      || (b.nom || '').toLowerCase().includes(q)
      || (b.categorie || '').toLowerCase().includes(q);
    return inStock && matchSearch;
  });

  // Sélection
  const toggleSelection = (b) => {
    setSelection(prev => {
      if (prev[b.id]) {
        const next = { ...prev };
        delete next[b.id];
        return next;
      }
      return { ...prev, [b.id]: 1 };
    });
  };

  const setQty = (id, qty) => {
    const q = Math.max(1, Math.min(99, parseInt(qty) || 1));
    setSelection(prev => ({ ...prev, [id]: q }));
  };

  const totalEtiquettes = Object.entries(selection).reduce((acc, [, qty]) => acc + qty, 0);

  // Bijoux sélectionnés avec détail
  const selectionDetail = bijoux.filter(b => selection[b.id]);

  // Imprimer
  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = 'etiquette-print-style';
    style.innerHTML = `
      @media print {
        @page {
          size: 35mm 14mm;
          margin: 0;
        }
        body * { visibility: hidden !important; }
        #etiquettes-print-zone,
        #etiquettes-print-zone * { visibility: visible !important; }
        #etiquettes-print-zone {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 35mm !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .etiquette-bijou {
          page-break-after: always;
          break-after: page;
        }
        .etiquette-bijou:last-child {
          page-break-after: avoid;
          break-after: avoid;
        }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => {
      const s = document.getElementById('etiquette-print-style');
      if (s) s.remove();
    }, 1000);
  };

  const selectAll = () => {
    const next = {};
    bijouFiltres.forEach(b => { next[b.id] = selection[b.id] || 1; });
    setSelection(next);
  };

  const clearSelection = () => setSelection({});

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* ── En-tête ── */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-100 rounded-lg">
            <Tag className="w-6 h-6 text-yellow-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Impression Étiquettes</h1>
            <p className="text-sm text-gray-500">Format 3.5cm × 1.4cm — Code-barre CODE128</p>
          </div>
        </div>
        {totalEtiquettes > 0 && (
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow transition"
          >
            <Printer className="w-4 h-4" />
            Imprimer {totalEtiquettes} étiquette{totalEtiquettes > 1 ? 's' : ''}
          </button>
        )}
      </div>

      <div className="flex gap-4">
        {/* ── Panneau gauche : sélection ── */}
        <div className="flex-1 min-w-0">
          {/* Barre de recherche + filtres */}
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par référence, pièce, nom..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <select
              value={filtreStatut}
              onChange={e => setFiltreStatut(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-yellow-400 focus:outline-none"
            >
              <option value="stock">En stock</option>
              <option value="tous">Tous les bijoux</option>
            </select>
            <button
              onClick={selectAll}
              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition"
            >
              Tout sélectionner
            </button>
            {Object.keys(selection).length > 0 && (
              <button
                onClick={clearSelection}
                className="text-xs bg-red-50 hover:bg-red-100 text-red-600 px-3 py-2 rounded-lg transition"
              >
                Tout effacer
              </button>
            )}
          </div>

          {/* Compteur */}
          <div className="text-xs text-gray-400 mb-2">
            {loading ? 'Chargement...' : `${bijouFiltres.length} bijou${bijouFiltres.length > 1 ? 'x' : ''} — ${Object.keys(selection).length} sélectionné${Object.keys(selection).length > 1 ? 's' : ''}`}
          </div>

          {/* Liste bijoux */}
          <div
            className="border border-gray-200 rounded-xl overflow-y-auto bg-white"
            style={{ maxHeight: 'calc(100vh - 240px)' }}
          >
            {loading ? (
              <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                Chargement des bijoux…
              </div>
            ) : bijouFiltres.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                Aucun bijou trouvé
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="py-2 px-3 w-8"></th>
                    <th className="py-2 px-3">Pièce / Référence</th>
                    <th className="py-2 px-3">Bijou</th>
                    <th className="py-2 px-3">Métal</th>
                    <th className="py-2 px-3 text-right">Prix</th>
                    <th className="py-2 px-3 text-center w-24">Quantité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bijouFiltres.map(b => {
                    const selected = !!selection[b.id];
                    return (
                      <tr
                        key={b.id}
                        onClick={() => toggleSelection(b)}
                        className={`cursor-pointer transition ${selected ? 'bg-yellow-50 border-l-2 border-l-yellow-400' : 'hover:bg-gray-50'}`}
                      >
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleSelection(b)}
                            onClick={e => e.stopPropagation()}
                            className="rounded accent-yellow-500"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-mono text-xs font-bold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded inline-block">
                            {b.id_produit || '—'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{b.reference_article || b.reference || ''}</div>
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-medium text-gray-800 truncate max-w-[140px]">
                            {b.nom || b.categorie || '—'}
                          </div>
                          <div className="text-xs text-gray-400">{b.categorie || ''}</div>
                        </td>
                        <td className="py-2 px-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (b.couleur_or || '').toUpperCase() === 'JAUNE' ? 'bg-yellow-100 text-yellow-800' :
                            (b.couleur_or || '').toUpperCase() === 'BLANC' ? 'bg-gray-100 text-gray-700' :
                            (b.couleur_or || '').toUpperCase() === 'ROSE' ? 'bg-pink-100 text-pink-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {b.matiere || b.couleur_or || 'OR'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-800">
                          {b.prix_vente ? `${parseFloat(b.prix_vente).toFixed(0)} DH` : '—'}
                        </td>
                        <td className="py-2 px-3" onClick={e => e.stopPropagation()}>
                          {selected ? (
                            <div className="flex items-center gap-1 justify-center">
                              <button
                                onClick={() => setQty(b.id, (selection[b.id] || 1) - 1)}
                                className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <input
                                type="number"
                                min={1} max={99}
                                value={selection[b.id] || 1}
                                onChange={e => setQty(b.id, e.target.value)}
                                className="w-10 text-center text-sm border border-gray-300 rounded py-0.5 font-semibold"
                              />
                              <button
                                onClick={() => setQty(b.id, (selection[b.id] || 1) + 1)}
                                className="w-6 h-6 flex items-center justify-center rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-center text-gray-300 text-xs">—</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Panneau droit : Aperçu étiquettes ── */}
        <div className="w-80 shrink-0">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sticky top-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                <Tag className="w-4 h-4 text-yellow-600" />
                Aperçu ({totalEtiquettes} pcs)
              </h3>
              {totalEtiquettes > 0 && (
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs px-3 py-1.5 rounded-lg font-semibold transition"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Imprimer
                </button>
              )}
            </div>

            {totalEtiquettes === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-xs text-center gap-2">
                <Tag className="w-8 h-8 text-gray-200" />
                Sélectionnez des bijoux<br />pour voir l'aperçu
              </div>
            ) : (
              <div
                className="overflow-y-auto"
                style={{ maxHeight: 'calc(100vh - 280px)' }}
              >
                <div className="space-y-3">
                  {selectionDetail.map(b => (
                    <div key={b.id} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium">{b.id_produit} — ×{selection[b.id]}</span>
                        <button
                          onClick={() => toggleSelection(b)}
                          className="text-gray-300 hover:text-red-400"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {/* Aperçu étiquette — taille réduite pour l'écran */}
                      <div style={{ transform: 'scale(1.8)', transformOrigin: 'top left', marginBottom: '14px', marginRight: '-10px' }}>
                        <EtiquetteItem bijou={b} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Zone d'impression (cachée à l'écran) ── */}
      <div id="etiquettes-print-zone" ref={printRef} style={{ display: 'none' }}>
        {selectionDetail.map(b =>
          Array.from({ length: selection[b.id] || 1 }).map((_, idx) => (
            <EtiquetteItem key={`${b.id}-${idx}`} bijou={b} />
          ))
        )}
      </div>
    </div>
  );
}
